import { useAuth } from '@contexts/auth-provider';

import type { TokenData } from '../../AuthProvider.types';

/**
 * Hook for components that require authenticated user
 *
 * Use this instead of useAuth() in components that:
 * - Are behind AuthenticatedRoute
 * - Should ONLY render when user is authenticated
 * - Need to access user data without null checks
 *
 * Returns non-nullable TokenData - throws if user not authenticated
 *
 * @returns TokenData (never null)
 * @throws Error if user is not authenticated
 *
 * @example
 * // ✅ Good - in authenticated component
 * const { firstName, lastName } = useAuthenticatedUser();
 *
 * @example
 * // ❌ Bad - use useAuth() instead in public components
 * const auth = useAuth();
 * if (!auth.isAuthenticated) return <LoginButton />;
 */
export function useAuthenticatedUser(): TokenData {
  const { authData, isAuthenticated } = useAuth();

  if (!isAuthenticated || !authData) {
    throw new Error(
      'useAuthenticatedUser() called in unauthenticated context. ' +
        'This component should be behind AuthenticatedRoute or check isAuthenticated first.'
    );
  }

  return authData;
}
